<template>
  <div class="anon">
    <div class="anon-hero">
      <span class="anon-hero-badge">
        <img src="/assets/gifting/icon-premium.svg" alt="" />
        Premium Feature
      </span>
      <h2>Anonymous <span>Gifting</span></h2>
      <p>
        Send gifts without revealing your identity. Keep your profile private
        while spreading motivation.
      </p>
    </div>

    <ul class="anon-usps">
      <li v-for="usp in usps" :key="usp.title">
        <span class="anon-usp-icon">
          <img :src="usp.icon" alt="" />
        </span>
        <strong>{{ usp.title }}</strong>
        <small>{{ usp.text }}</small>
      </li>
    </ul>

    <div v-if="subscription" class="anon-active">
      <div>
        <span
          class="anon-active-label"
          :class="{ 'is-cancelled': subscription.ending }"
        >
          {{
            subscription.ending
              ? "Subscription cancelled"
              : "Active subscription"
          }}
        </span>
        <h3>{{ subscription.planName }}</h3>
        <p>
          {{ subscription.price }} {{ subscription.period }} · started
          {{ formatDate(subscription.startedAt) }}
          <span v-if="subscription.retrying" class="anon-retry">
            · payment failed, Stripe is retrying
          </span>
          <span v-else-if="subscription.ending" class="anon-until">
            · will not renew, you keep access until
            {{ formatDate(subscription.endsAt) }}
          </span>
          <span v-else-if="subscription.endsAt">
            · renews {{ formatDate(subscription.endsAt) }}
          </span>
        </p>
      </div>
      <template v-if="subscription.planId !== 'lifetime'">
        <b-button
          v-if="!subscription.ending"
          class="anon-cancel"
          type="button"
          :disabled="cancelling"
          @click="cancelSubscription"
        >
          {{ cancelling ? "Cancelling…" : "Cancel Subscription" }}
        </b-button>
        <b-button
          v-else
          class="anon-cancel anon-resume"
          type="button"
          :disabled="cancelling"
          @click="resumeSubscription"
        >
          {{ cancelling ? "Resuming…" : "Resume Subscription" }}
        </b-button>
      </template>
    </div>

    <div v-if="verifying" class="anon-notice">
      Confirming your payment with Stripe…
    </div>
    <div v-else-if="cancelled" class="anon-notice">
      Checkout cancelled — you have not been charged.
    </div>
    <div v-else-if="failed" class="anon-alert">
      We could not confirm your payment. If you were charged it will appear here
      once Stripe settles — otherwise please try again.
    </div>

    <div class="anon-plans">
      <div
        v-for="plan in plans"
        :key="plan.id"
        class="anon-plan"
        :class="{
          selected: selectedPlan === plan.id,
          disabled: isDisabled(plan),
        }"
        @click="selectPlan(plan)"
      >
        <div class="anon-plan-head">
          <span class="anon-radio" :class="{ on: selectedPlan === plan.id }"></span>
          <h3>{{ plan.name }}</h3>
          <span v-if="plan.badge" class="anon-best">
            <img src="/assets/gifting/icon-crown.svg" alt="" />
            {{ plan.badge }}
          </span>
        </div>

        <div class="anon-price">
          <strong v-if="plan.price">{{ plan.price }}</strong>
          <strong v-else class="anon-price-empty">—</strong>
          <span>/ {{ plan.period }}</span>
        </div>

        <ul class="anon-perks">
          <li v-for="(perk, index) in plan.perks" :key="index">
            <span class="anon-check"></span>
            {{ perk }}
          </li>
        </ul>

        <p v-if="isOwned(plan.id)" class="anon-plan-note">
          {{
            subscription.ending
              ? `Active until ${formatDate(subscription.endsAt)}.`
              : "You are subscribed to this plan."
          }}
        </p>
        <p v-else-if="isSuperseded(plan.id)" class="anon-plan-note">
          Already included in your Lifetime plan.
        </p>
      </div>
    </div>

    <div class="anon-cta-wrap">
      <b-button
        class="anon-cta"
        type="button"
        :disabled="ctaDisabled"
        @click="continueToPayment"
      >
        {{ ctaLabel }}
      </b-button>
      <p class="anon-secure">
        <img src="/assets/gifting/icon-lock.svg" alt="" />
        Secure payment powered by Stripe
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  GIFTING_PLANS,
  pricingForPlan,
  takeCheckoutReturn,
} from "@/utils/gifting";

export default {
  name: "AnonymousGifting",
  data() {
    return {
      subscription: null,
      selectedPlan: "lifetime",
      busyPlan: null,
      failed: false,
      cancelled: false,
      verifying: false,
      subscriptionLoaded: false,
      cancelling: false,
      usps: [
        {
          title: "Hide your name",
          text: "The recipient won't see your profile.",
          icon: "/assets/gifting/feature-hide.svg",
        },
        {
          title: "Support others",
          text: "Send motivation anonymously.",
          icon: "/assets/gifting/feature-gift.svg",
        },
        {
          title: "Keep your privacy",
          text: "Your identity stays safe and secure.",
          icon: "/assets/gifting/feature-privacy.svg",
        },
        {
          title: "Great experience",
          text: "All gifting features remain the same.",
          icon: "/assets/gifting/feature-infinity.svg",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("gifting", { giftingPlans: "plans" }),
    plans() {
      return GIFTING_PLANS.map((plan) => {
        const pricing = pricingForPlan(plan.id, this.giftingPlans);
        return {
          ...plan,
          pricing,
          period: pricing?.period || plan.period,
          price: pricing ? `${pricing.currency} ${pricing.price}` : null,
        };
      });
    },
    activePlan() {
      return this.plans.find((plan) => plan.id === this.selectedPlan) || null;
    },
    ctaDisabled() {
      const plan = this.activePlan;
      return (
        !plan ||
        !plan.pricing ||
        this.isDisabled(plan) ||
        !!this.busyPlan ||
        this.verifying ||
        !this.subscriptionLoaded
      );
    },
    ctaLabel() {
      if (this.busyPlan) return "Redirecting…";
      const plan = this.activePlan;
      if (plan && this.isOwned(plan.id)) {
        return this.subscription?.ending
          ? `Ends ${this.formatDate(this.subscription.endsAt)}`
          : "Already subscribed";
      }
      if (plan && this.isSuperseded(plan.id)) return "Included in Lifetime";
      if (plan && this.isUpgrade(plan.id)) return "Upgrade to Lifetime";
      return "Continue to Payment";
    },
  },
  methods: {
    isOwned(planId) {
      return this.subscription?.planId === planId;
    },
    isSuperseded(planId) {
      return planId === "monthly" && this.subscription?.planId === "lifetime";
    },
    isUpgrade(planId) {
      return planId === "lifetime" && this.subscription?.planId === "monthly";
    },
    isDisabled(plan) {
      return this.isOwned(plan.id) || this.isSuperseded(plan.id);
    },
    selectPlan(plan) {
      if (this.isSuperseded(plan.id)) return;
      this.selectedPlan = plan.id;
    },
    formatDate(value) {
      return value
        ? new Date(value).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "";
    },
    onPageShow(event) {
      if (event.persisted) this.busyPlan = null;
    },
    async continueToPayment() {
      const plan = this.activePlan;
      if (!plan || this.ctaDisabled) return;

      this.busyPlan = plan.id;
      this.failed = false;
      this.cancelled = false;

      try {
        const { data } = await this.$store.dispatch(
          "gifting/startGiftingCheckout",
          { plan_id: plan.id }
        );

        if (!data?.session_url) throw new Error("Session URL not found");
        window.location.href = data.session_url;
      } catch (error) {
        this.busyPlan = null;
      }
    },
    async resumeSubscription() {
      this.cancelling = true;

      const res = await this.$store.dispatch(
        "gifting/resumeGiftingSubscription"
      );

      this.cancelling = false;

      if (!res?.status) return;

      this.subscription = this.mapSubscription(res.data);
    },
    async cancelSubscription() {
      this.cancelling = true;

      const res = await this.$store.dispatch(
        "gifting/cancelGiftingSubscription"
      );

      this.cancelling = false;

      if (!res?.status) return;

      this.subscription = this.mapSubscription(res.data);
    },
    mapSubscription(data) {
      if (!data) return null;

      return {
        planId: data.plan_id,
        planName: data.plan_name,
        price: `${data.currency} ${data.amount}`,
        period: data.plan_id === "monthly" ? "per month" : "one time purchase",
        retrying: data.retrying,
        ending: data.ending,
        startedAt: data.started_at,
        endsAt: data.ends_at
          ? new Date(data.ends_at * 1000).toISOString()
          : null,
      };
    },
    async loadSubscription() {
      const res = await this.$store.dispatch("gifting/getGiftingSubscription");
      this.subscriptionLoaded = true;
      if (!res?.status) return;

      this.subscription = this.mapSubscription(res.data.subscription);
    },
    async readCheckoutReturn() {
      const captured = takeCheckoutReturn();
      if (!captured) return;

      const { result, orderId } = captured;

      const query = { ...this.$route.query };
      delete query.gifting_result;
      delete query.gifting_order;
      this.$router
        .replace({ path: this.$route.path, query, hash: "#gifting" })
        .catch(() => {});

      if (result !== "success" || !orderId) {
        this.cancelled = result === "error";
        this.failed = !this.cancelled;
        return;
      }

      this.verifying = true;

      const res = await this.$store.dispatch("gifting/verifyGiftingCheckout", {
        order_id: orderId,
      });

      this.verifying = false;

      if (!res?.status || !res.data?.paid) this.failed = true;
    },
  },
  async created() {
    window.addEventListener("pageshow", this.onPageShow);
    this.$store.dispatch("gifting/getGiftingPlans");

    await this.readCheckoutReturn();
    await this.loadSubscription();

    if (this.subscription?.planId === "lifetime") this.selectedPlan = "lifetime";
  },
  beforeDestroy() {
    window.removeEventListener("pageshow", this.onPageShow);
  },
};
</script>

<style scoped>
.anon-hero {
  background-color: #0b0b0d;
  background-image: url("/assets/gifting/hero-anonymous.png");
  background-position: right center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 12px;
  min-height: 232px;
  padding: 34px 36px;
}

.anon-hero-badge {
  align-items: center;
  background-color: var(--gift-gold);
  border-radius: 999px;
  color: var(--gift-ink);
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.6px;
  margin-bottom: 16px;
  padding: 5px 12px;
  text-transform: uppercase;
}

.anon-hero h2 {
  color: #ffffff;
  font-size: 34px;
  font-weight: 700;
  margin-bottom: 12px;
}

.anon-hero h2 span {
  color: var(--gift-gold);
}

.anon-hero p {
  color: #d4d4d8;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  max-width: 420px;
}

.anon-usps {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(4, 1fr);
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
}

.anon-usps li {
  text-align: center;
}

.anon-usp-icon {
  display: inline-block;
  height: 40px;
  margin-bottom: 12px;
  width: 40px;
}

.anon-usp-icon img,
.anon-usp-icon svg {
  height: 40px;
  width: 40px;
}

.anon-usps strong {
  color: var(--gift-ink);
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.anon-usps small {
  color: var(--gift-muted);
  font-size: 13px;
}

.anon-active {
  align-items: center;
  background-color: #ffffff;
  border: 1px solid #f0e2bd;
  border-left: 4px solid var(--gift-gold);
  border-radius: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: space-between;
  margin-top: 28px;
  padding: 16px 20px;
}

.anon-active-label {
  color: #b98a12;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.anon-active h3 {
  color: var(--gift-ink);
  font-size: 18px;
  font-weight: 700;
  margin: 2px 0;
}

.anon-active p {
  color: var(--gift-muted);
  font-size: 14px;
  margin: 0;
}

.anon-retry {
  color: #a3352b;
}

.anon-active-label.is-cancelled {
  color: #8a6210;
}

.anon-until {
  color: #8a6210;
  font-weight: 500;
}

.anon-resume {
  border-color: var(--gift-gold);
}

.anon-resume:hover:not(:disabled),
.anon-resume:focus:not(:disabled) {
  background-color: var(--gift-gold);
  border-color: var(--gift-gold);
  color: var(--gift-ink);
}

.anon-cancel {
  align-items: center;
  align-self: center;
  background-color: transparent;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  color: var(--gift-ink);
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 15px;
  height: 40px;
  justify-content: center;
  line-height: 1;
  padding: 0 18px;
  white-space: nowrap;
  width: auto;
}

.anon-cancel:hover:not(:disabled),
.anon-cancel:focus:not(:disabled) {
  background-color: var(--gift-ink);
  border-color: var(--gift-ink);
  color: #ffffff;
}

.anon-notice,
.anon-alert {
  border-radius: 10px;
  font-size: 15px;
  margin-top: 24px;
  padding: 14px 18px;
}

.anon-notice {
  background-color: #fff6e0;
  color: #8a6210;
}

.anon-alert {
  background-color: #fdecea;
  color: #a3352b;
}

.anon-plans {
  display: grid;
  gap: 22px;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 28px;
}

.anon-plan {
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  padding: 22px 24px 24px;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.anon-plan.selected {
  background-color: #fffbeb;
  border-color: var(--gift-gold);
  box-shadow: 0 0 0 1px var(--gift-gold) inset;
}

.anon-plan.disabled {
  cursor: default;
  opacity: 0.7;
}

.anon-plan-head {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
}

.anon-plan-head h3 {
  color: var(--gift-ink);
  font-size: 19px;
  font-weight: 700;
  flex: 1 1 auto;
  margin: 0;
}

.anon-radio {
  border: 2px solid #d1d5db;
  border-radius: 50%;
  flex: 0 0 auto;
  height: 20px;
  position: relative;
  width: 20px;
}

.anon-radio.on {
  border-color: var(--gift-gold);
}

.anon-radio.on::after {
  background-color: var(--gift-gold);
  border-radius: 50%;
  content: "";
  height: 10px;
  left: 3px;
  position: absolute;
  top: 3px;
  width: 10px;
}

.anon-best {
  align-items: center;
  background-color: var(--gift-gold);
  border-radius: 999px;
  color: var(--gift-ink);
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  gap: 5px;
  letter-spacing: 0.5px;
  padding: 5px 12px;
  text-transform: uppercase;
}

.anon-price {
  align-items: baseline;
  border-bottom: 1px solid #ececec;
  display: flex;
  gap: 8px;
  padding-bottom: 18px;
}

.anon-price strong {
  color: var(--gift-ink);
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
}

.anon-price-empty {
  color: #d1d5db;
}

.anon-price span {
  color: #9ca3af;
  font-size: 15px;
}

.anon-perks {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
}

.anon-perks li {
  align-items: center;
  color: #374151;
  display: flex;
  font-size: 15px;
  gap: 10px;
}

.anon-perks li + li {
  margin-top: 12px;
}

.anon-check {
  background-color: var(--gift-gold);
  border-radius: 50%;
  flex: 0 0 auto;
  height: 18px;
  position: relative;
  width: 18px;
}

.anon-check::after {
  border-bottom: 2px solid var(--gift-ink);
  border-left: 2px solid var(--gift-ink);
  content: "";
  height: 4px;
  left: 5px;
  position: absolute;
  top: 5px;
  transform: rotate(-45deg);
  width: 8px;
}

.anon-plan-note {
  color: var(--gift-disabled-ink);
  font-size: 13px;
  margin: 16px 0 0;
}

.anon-cta-wrap {
  margin-top: 30px;
  text-align: center;
}

.anon-cta {
  background-image: var(--gift-gradient);
  border: 0;
  border-radius: 999px;
  color: var(--gift-ink);
  font-size: 16px;
  font-weight: 700;
  height: 52px;
  letter-spacing: 0.4px;
  max-width: 560px;
  text-transform: uppercase;
  width: 100%;
}

.anon-cta:disabled {
  background-image: none;
  background-color: var(--gift-disabled-bg);
  color: var(--gift-disabled-ink);
  opacity: 1;
}

.anon-secure {
  align-items: center;
  color: #9ca3af;
  display: flex;
  font-size: 13px;
  gap: 6px;
  justify-content: center;
  margin: 14px 0 0;
}

@media (max-width: 767px) {
  .anon-usps {
    grid-template-columns: repeat(2, 1fr);
  }

  .anon-plans {
    grid-template-columns: 1fr;
  }

  .anon-hero {
    background-position: 78% center;
  }
}
</style>
